import { useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useAuthStore } from '../store/auth.ts';
import { dv } from '../i18n/dv.ts';

export function AcceptInvite(): JSX.Element {
  const navigate = useNavigate();
  const { token } = useParams({ from: '/invite/$token' });
  const reg = useAuthStore((s) => s.registerViaInvite);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError(dv.auth.passwordMismatch);
      return;
    }
    setPending(true);
    try {
      await reg(token, password, name);
      await navigate({ to: '/' });
    } catch {
      setError(dv.common.error);
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="mx-auto max-w-sm px-4 py-12">
      <div className="rounded-3xl bg-[hsl(var(--chrome))] p-6 lg:p-7 space-y-6">
        <h1 className="text-2xl font-bold tracking-tight">{dv.auth.acceptInviteTitle}</h1>
        <form className="space-y-4" onSubmit={submit}>
          <div className="space-y-1.5">
            <label htmlFor="ai-name" className="field-label">{dv.auth.yourName}</label>
            <input
              id="ai-name"
              className="input"
              aria-label={dv.auth.yourName}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="ai-password" className="field-label">{dv.auth.newPassword}</label>
            <input
              id="ai-password"
              className="input"
              type="password"
              dir="ltr"
              aria-label={dv.auth.newPassword}
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="ai-confirm" className="field-label">{dv.auth.confirmPassword}</label>
            <input
              id="ai-confirm"
              className="input"
              type="password"
              dir="ltr"
              aria-label={dv.auth.confirmPassword}
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </div>
          {error && (
            <div role="alert" className="rounded-2xl bg-[hsl(var(--error-container))] px-3.5 py-2.5 text-sm text-destructive">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={pending}
            className="btn-primary w-full"
          >
            {pending ? dv.common.loading : dv.auth.acceptInvite}
          </button>
        </form>
      </div>
    </div>
  );
}
